import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dateToString } from '../shared/formatters';
import { loadingStart, loadingEnd, showMessage } from './appMainSlice';
import { logoff } from './accountSlice';

/**
 * Returns the current month as 'YYYY-MM'.
 */
export const getCurrentMonthValue = (date = new Date()) => {
  return dateToString(date).substring(0, 7);
};

/**
 * Converts a 'YYYY-MM' value into the first and last day of that month.
 */
export const getMonthDateRange = (monthValue) => {
  const [year, month] = (monthValue || getCurrentMonthValue()).split('-').map(value => parseInt(value, 10));
  return {
    from: dateToString(new Date(year, month - 1, 1)),
    to: dateToString(new Date(year, month, 0))
  };
};

/**
 * Converts a 'YYYY-MM' value into the report column key ('MM/YYYY').
 */
export const getMonthKey = (monthValue) => {
  const [year, month] = (monthValue || getCurrentMonthValue()).split('-');
  return `${month}/${year}`;
};

/**
 * Returns the month before the given 'YYYY-MM' value as 'YYYY-MM'.
 */
export const getPreviousMonthValue = (monthValue) => {
  const [year, month] = (monthValue || getCurrentMonthValue()).split('-').map(value => parseInt(value, 10));
  return getCurrentMonthValue(new Date(year, month - 2, 1));
};

const today = new Date();
const initialState = {
  result: {},
  transactions: [],
  viewMode: 'grouped',
  shouldBeUpdated: true,
  lastUpdate: today.toISOString(),
  showFilters: window.innerWidth > 600,
  filterFields: {
    month: getCurrentMonthValue(today),
    users: [],
    financeAccounts: []
  }
};

// Async thunks
export const loadStart = createAsyncThunk(
  'monthlyBalance/loadStart',
  async (_, { dispatch, getState, extra: api }) => {
    dispatch(loadingStart());
    try {
      const { month, users, financeAccounts } = getState().monthlyBalance.filterFields;
      const currentMonthRange = { ...getMonthDateRange(month), users, financeAccounts };
      // The report also brings the previous month so groups and categories can be
      // compared with it; the transactions stay limited to the selected month.
      const reportFilters = {
        ...currentMonthRange,
        from: getMonthDateRange(getPreviousMonthValue(month)).from
      };
      const [result, transactionResult] = await Promise.all([
        api.loadBalancePerCategory(reportFilters),
        api.loadTransaction(currentMonthRange)
      ]);
      dispatch(loadingEnd());
      return { result, transactions: transactionResult.items || [] };
    } catch (error) {
      dispatch(loadingEnd());
      if (error.isAxiosError) {
        dispatch(logoff());
        throw error;
      }
      dispatch(showMessage(error));
      throw error;
    }
  }
);

const monthlyBalanceSlice = createSlice({
  name: 'monthlyBalance',
  initialState,
  reducers: {
    updateFilters: (state, action) => {
      state.shouldBeUpdated = true;
      state.showFilters = true;
      state.filterFields = { ...action.payload };
    },
    toogleFilters: (state) => {
      state.showFilters = !state.showFilters;
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    clear: () => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadStart.pending, (state) => {
        state.shouldBeUpdated = false;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(loadStart.fulfilled, (state, action) => {
        state.result = action.payload.result;
        state.transactions = action.payload.transactions;
        state.shouldBeUpdated = false;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(loadStart.rejected, (state) => {
        state.shouldBeUpdated = true;
      });
  }
});

export const { updateFilters, toogleFilters, setViewMode, clear } = monthlyBalanceSlice.actions;

export default monthlyBalanceSlice.reducer;
